import { BitmapCoordinatesRenderingScope, CanvasRenderingTarget2D } from 'fancy-canvas'
import DrawingBase from '../drawing.base'
import {
    ColorValue,
    DrawingSettingGroup,
    DrawingTypes,
    LabelAxisViewConfig,
    Point,
    PointCoordinates,
    SettingValues
} from '../drawing.interface'
import { PrimitiveHoveredItem, Time } from 'lightweight-charts'
import { SettingType } from '../drawing.enums'
import { getDistanceBetweenPoints, getDistanceFromLine, scaledCoordinate } from '../drawing.helper'
import { primaryDrawingColor } from '../../../../assets/colors'

export type EllipseDrawingState = {
    p1?: Point,
    p2?: Point,
    secondaryRadiusRatio?: number,
}

export type EllipseDrawingSettings = {
    fillColor: ColorValue,
    strokeColor: ColorValue,
    strokeWidth: number,
    labelColor: ColorValue,
    point1: Point,
    point2: Point
}

class EllipseDrawingTool extends DrawingBase<any, EllipseDrawingSettings, EllipseDrawingState> {
    primaryRadius: number = 0
    rotation: number = 0
    secondaryRadius: number = 0
    primarySlope = 0
    primaryIntercept = 0
    secondarySlope = 0
    scaledCoordinates: Record<string, PointCoordinates> = {}
    primaryRadiusChanged = false
    p3?: Point
    p4?: Point

    draw(scope: BitmapCoordinatesRenderingScope, target: CanvasRenderingTarget2D) {
        const ctx = scope.context
        let { p1, p2 } = this._state
        this.calculateAttributes()
        const { center, p1: p1s, p2: p2s } = this.scaledCoordinates
        if (!p1 || !p2) return
        if (!p1s || !p2s || !center) return
        if (!this.secondaryRadius) {
            ctx.beginPath()
            ctx.moveTo(p1s.x, p1s.y)
            ctx.lineTo(p2s.x, p2s.y)
        } else {
            ctx.beginPath()
            if (!center) return
            ctx.fillStyle = this._settings.fillColor.color
            ctx.globalAlpha = this._settings.fillColor.opacity
            ctx.ellipse(center.x, center.y, this.primaryRadius, this.secondaryRadius, this.rotation, 0, 2 * Math.PI)
            ctx.fill()

        }
        ctx.globalAlpha = this._settings.strokeColor.opacity
        ctx.lineWidth = this._settings.strokeWidth * window.devicePixelRatio
        ctx.strokeStyle = this._settings.strokeColor.color
        ctx.stroke()
        ctx.closePath()
    };

    calculateAttributes() {
        const { p1, p2, secondaryRadiusRatio } = this._state

        if (!p1 || !p2) return

        const x1 = this.timeToCoordinate(p1!.x)
        const y1 = this.priceToCoordinate(p1!.y)
        const x2 = this.timeToCoordinate(p2!.x)
        const y2 = this.priceToCoordinate(p2!.y)

        if (!x1 || !x2 || !y1 || !y2) return

        const p1x = scaledCoordinate(x1, this.horizontalPixelRatio)
        const p2x = scaledCoordinate(x2, this.horizontalPixelRatio)
        const p1y = scaledCoordinate(y1, this.verticalPixelRatio)
        const p2y = scaledCoordinate(y2, this.verticalPixelRatio)

        this.scaledCoordinates.p1 = {
            x: p1x,
            y: p1y
        }
        this.scaledCoordinates.p2 = {
            x: p2x,
            y: p2y
        }

        this.primarySlope = (p2y - p1y) / (p2x - p1x)
        this.rotation = Math.atan(this.primarySlope)
        this.secondarySlope = -1 / this.primarySlope
        this.primaryIntercept = p1y - this.primarySlope * p1x

        const centerX = (p1x + p2x) / 2
        const centerY = (p1y + p2y) / 2
        this.scaledCoordinates.center = {
            x: centerX,
            y: centerY
        }

        const primaryDiameter = getDistanceBetweenPoints(p1x, p2x, p1y, p2y)
        this.primaryRadius = primaryDiameter / 2
        if (this.primaryRadiusChanged && this.secondaryRadius) {
            this.primaryRadiusChanged = false
            this._state.secondaryRadiusRatio = this.secondaryRadius / this.primaryRadius
        } else if (secondaryRadiusRatio) {
            this.secondaryRadius = this.primaryRadius * secondaryRadiusRatio
        }


        const sign = p2x > p1x ? 1 : -1
        const x3 = (sign * this.secondaryRadius) * Math.sin(-this.rotation) + centerX
        const y3 = (sign * this.secondaryRadius) * Math.cos(-this.rotation) + centerY

        this.p3 = {
            id: 'p3',
            x: 0 as Time,
            y: 0,
            coordinates: {
                x: x3,
                y: y3
            }
        }

        const x4 = (-sign * this.secondaryRadius) * Math.sin(-this.rotation) + centerX
        const y4 = (-sign * this.secondaryRadius) * Math.cos(-this.rotation) + centerY

        this.p4 = {
            id: 'p4',
            x: 0 as Time,
            y: 0,
            coordinates: {
                x: x4,
                y: y4
            }
        }
    }

    onAddPoint = (time: Time, price: number, coordinates: PointCoordinates, preview?: boolean) => {
        const point: Point = {
            id: 'p1',
            x: time,
            y: price
        }
        if (!this.lastAddedPoint) {
            this._state.p1 = point
        } else if (this.lastAddedPoint.id == 'p1') {
            point.id = 'p2'
            this._state.p2 = point
        } else if (this.lastAddedPoint.id == 'p2') {
            point.id = 'p3'
            point.coordinates = coordinates
            const { p1, p2 } = this._state

            const x1 = this.timeToCoordinate(p1!.x)
            const y1 = this.priceToCoordinate(p1!.y)
            const x2 = this.timeToCoordinate(p2!.x)
            const y2 = this.priceToCoordinate(p2!.y)

            if (!x1 || !x2 || !y1 || !y2) return

            const p1x = scaledCoordinate(x1, this.horizontalPixelRatio)
            const p2x = scaledCoordinate(x2, this.horizontalPixelRatio)
            const p1y = scaledCoordinate(y1, this.verticalPixelRatio)
            const p2y = scaledCoordinate(y2, this.verticalPixelRatio)
            const primaryDiameter = getDistanceBetweenPoints(p1x, p2x, p1y, p2y)
            const scaledX = scaledCoordinate(coordinates.x, this.horizontalPixelRatio)
            const scaledY = scaledCoordinate(coordinates.y, this.verticalPixelRatio)
            const secondaryRadius = getDistanceFromLine(scaledX, scaledY, this.primarySlope, this.primaryIntercept)
            this._state.secondaryRadiusRatio = Number((2 * secondaryRadius / primaryDiameter).toFixed(4))
            !preview && this.onDrawingEnd()
        }

        if (!preview) return { ...point }
    }


    getHandles = (): Point[] => {
        const handles: Point[] = []
        const { p1, p2 } = this._state
        const horizontalRatio = this.horizontalPixelRatio || 1
        const verticalRatio = this.verticalPixelRatio || 1
        const scaleToMediaCoordinates = (point: Point): Point => {
            if (!point.coordinates) return point
            return {
                ...point,
                coordinates: {
                    x: point.coordinates.x / horizontalRatio,
                    y: point.coordinates.y / verticalRatio
                }
            }
        }

        p1 && handles.push(p1)
        p2 && handles.push(p2)
        this.p3 && handles.push(scaleToMediaCoordinates(this.p3))
        this.p4 && handles.push(scaleToMediaCoordinates(this.p4))

        return handles
    }

    hitTest(x: number, y: number): PrimitiveHoveredItem | null {
        const { center } = this.scaledCoordinates

        if (!center || !this.secondaryRadius) return null

        const scaledX = scaledCoordinate(x, this.horizontalPixelRatio)
        const scaledY = scaledCoordinate(y, this.verticalPixelRatio)
        const rotatedX = (scaledX - center.x) * Math.cos(-this.rotation) + (scaledY - center.y) * Math.sin(this.rotation)
        const rotatedY = (scaledX - center.x) * Math.sin(-this.rotation) + (scaledY - center.y) * Math.cos(this.rotation)

        const d = Math.pow(rotatedX, 2) / Math.pow(this.primaryRadius, 2) + Math.pow(rotatedY, 2) / Math.pow(this.secondaryRadius, 2)

        if (d <= 1) {
            return {
                externalId: this.id,
                zOrder: 'normal'
            }
        }
        return null
    }

    updateHandles(points: Point[]) {
        points.forEach((h) => {
            switch (h.id) {
                case 'p1':
                    this._state.p1 = h
                    break
                case 'p2':
                    this._state.p2 = h
            }
        })
    }

    updateHandle = (id: string, time: Time, price: number, coordinates: PointCoordinates, syncMode = false) => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return
        switch (id) {
            case 'p1':
                p1.x = time
                p1.y = price
                this.primaryRadiusChanged = true
                break
            case 'p2':
                p2.x = time
                p2.y = price
                this.primaryRadiusChanged = true
                break
            case 'p3':
            case 'p4':
                if (syncMode) return
                const scaledX = scaledCoordinate(coordinates.x, this.horizontalPixelRatio)
                const scaledY = scaledCoordinate(coordinates.y, this.verticalPixelRatio)
                const secondaryRadius = getDistanceFromLine(scaledX, scaledY, this.primarySlope, this.primaryIntercept)
                this._state.secondaryRadiusRatio = secondaryRadius / this.primaryRadius
                this.publishSyncStateEvent()
        }
    }
    getPriceAxisLabelPoints = (): LabelAxisViewConfig<number>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const min = Math.min(p1.y, p2.y)
        const max = Math.max(p1.y, p2.y)
        if (!min || !max) return []
        return [{
            id: 'price',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    getTimeAxisLabelPoints = (): LabelAxisViewConfig<Time>[] => {
        const { p1, p2 } = this._state
        if (!p1 || !p2) return []
        const p1x = this.timeToCoordinate(p1.x)
        const p2x = this.timeToCoordinate(p2.x)

        if (!p1x || !p2x) return []
        let min: Time = p1.x, max: Time = p2.x
        if (p1x > p2x) {
            min = p2.x
            max = p1.x
        }
        if (!min || !max) return []

        return [{
            id: 'time',
            p1: min,
            p2: max,
            fill: true,
            fillColor: this._settings.fillColor.color,
            labelColors: {
                p1: this._settings.labelColor.color,
                p2: this._settings.labelColor.color
            }
        }]
    }

    onSettingsUpdate = (settings: Partial<EllipseDrawingSettings>) => {
        if (settings.point1) {
            this._state.p1 = settings.point1
        }
        if (settings.point2) {
            this._state.p2 = settings.point2
        }
    }

    getAvailableSettings = (): DrawingSettingGroup<EllipseDrawingSettings, SettingValues>[] => {
        return [{
            id: 'style',
            displayName: 'Style',
            visible: true,
            settings: [
                {
                    name: 'fillColor',
                    label: 'Fill Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: '#f23645',
                        opacity: 0.2
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'labelColor',
                    label: 'Label Color',
                    type: SettingType.COLOR,
                    defaultValue: {
                        color: primaryDrawingColor,
                        opacity: 1
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeColor',
                    label: 'Border Color',
                    type: SettingType.COLOR,
                    defaultValue: { color: '#f23645', opacity: 1 },
                    custom: true,
                    visible: true
                },
                {
                    name: 'strokeWidth',
                    label: 'Border Width',
                    type: SettingType.STROKE_WIDTH,
                    defaultValue: 2,
                    custom: true,
                    visible: true
                }
            ]
        }, {
            id: 'coordinates',
            displayName: 'Coordinates',
            visible: true,
            settings: [
                {
                    name: 'point1',
                    label: 'Point 1',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p1',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                },
                {
                    name: 'point2',
                    label: 'Point 2',
                    type: SettingType.COORDINATE,
                    defaultValue: {
                        id: 'p2',
                        x: 0 as Time,
                        y: 0
                    },
                    custom: true,
                    visible: true
                }
            ]
        }]
    }

    getSettingsValues(): EllipseDrawingSettings {
        const settings = { ...this._settings }
        if (this._state.p1) {
            settings.point1 = this._state.p1
        }
        if (this._state.p2) {
            settings.point2 = this._state.p2
        }
        return settings
    }

    getMiniSettings = () => {
        const settings = this.getAvailableSettings()
        let miniSettings: (keyof EllipseDrawingSettings)[] = []

        settings.forEach((s) => {
            if (s.id === 'style') {
                miniSettings = [...s.settings.map(setting => setting.name)]
            }
        })

        return miniSettings
    }

    getSyncState = (): Partial<EllipseDrawingState> => {
        return {
            secondaryRadiusRatio: this._state.secondaryRadiusRatio
        }
    }

    getDrawingType(): DrawingTypes {
        return 'ellipse'
    }
}

export default EllipseDrawingTool